<template>
    <PopperNew
        v-model="open"
        :placement="placement"
        :offset="offset"
        :constrain-to-viewport="constrainToViewport"
        :constrain-padding="constrainPadding"
        :z-index="zIndex"
        :avoid-popper-elements="avoidPopperElements"
    >
        <template #default="slotProps">
            <button
                class="app-switcher"
                :class="{ 'app-switcher--open': open }"
                :style="triggerStyles"
                v-bind="slotProps"
                @click.stop="open = !open"
            >
                <span class="app-switcher__icon">
                    <img
                        v-if="currentApp?.logo"
                        :src="currentApp.logo"
                        :alt="currentApp.name"
                    />
                </span>
                <span class="app-switcher__name">{{ currentApp?.name }}</span>
                <IconNew
                    :icon="open ? 'ChevronUp' : 'ChevronDown'"
                    class="app-switcher__chevron"
                />
            </button>
        </template>

        <template #content>
            <PopperContentNew>
                <div class="app-switcher-menu" :style="menuStyles">
                    <a
                        v-for="app in apps"
                        :key="app.id"
                        :href="app.href || '#'"
                        class="app-switcher-menu__item"
                        :class="{
                            'app-switcher-menu__item--active':
                                app.id === currentApp?.id,
                            'app-switcher-menu__item--disabled': app.disabled
                        }"
                        @click.stop.prevent="selectApp(app)"
                    >
                        <span class="app-switcher-menu__logo">
                            <img :src="app.logo" :alt="app.name" />
                        </span>
                        <span class="app-switcher-menu__info">
                            <span class="app-switcher-menu__title">
                                {{ app.name }}
                            </span>
                            <span
                                v-if="app.description"
                                class="app-switcher-menu__desc"
                            >
                                {{ app.description }}
                            </span>
                        </span>
                    </a>
                </div>
            </PopperContentNew>
        </template>
    </PopperNew>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import IconNew from './Icon.vue';
import PopperNew from './Popper/Popper.vue';
import PopperContentNew from './Popper/PopperContent.vue';

export type AppSwitcherItem = {
    id: string | number;
    name: string;
    description?: string;
    logo: string;
    href?: string;
    disabled?: boolean;
};

const open = defineModel<boolean>({ default: false });

const props = defineProps<{
    apps?: AppSwitcherItem[];
    currentAppId?: string | number | null;
    placement?: string;
    offset?: number | { mainAxis?: number; crossAxis?: number };
    constrainToViewport?: boolean;
    constrainPadding?:
        | number
        | { top?: number; right?: number; bottom?: number; left?: number };
    avoidPopperElements?: boolean;
    zIndex?: number;
    triggerMinWidth?: string;
    menuWidth?: string;
}>();

const emit = defineEmits<{
    (e: 'select', app: AppSwitcherItem): void;
}>();

const apps = computed(() => props.apps ?? []);
const placement = computed(() => props.placement ?? 'bottom-start');
const offset = computed(() => props.offset ?? 8);
const constrainToViewport = computed(() => props.constrainToViewport ?? true);
const constrainPadding = computed(() => props.constrainPadding ?? 16);
const avoidPopperElements = computed(() => props.avoidPopperElements ?? false);
const zIndex = computed(() => props.zIndex ?? 200);
const triggerMinWidth = computed(() => props.triggerMinWidth ?? '130px');
const menuWidth = computed(() => props.menuWidth ?? '325px');

const currentApp = computed(() => {
    if (!apps.value.length) return null;

    return (
        apps.value.find((item) => item.id === props.currentAppId) ??
        apps.value[0]
    );
});

const triggerStyles = computed(() => ({
    minWidth: triggerMinWidth.value
}));

const menuStyles = computed(() => ({
    width: menuWidth.value
}));

const selectApp = (app: AppSwitcherItem) => {
    if (app.disabled) return;
    open.value = false;
    emit('select', app);
};
</script>

<style lang="scss" scoped>
@import '../assets/styles/_main.scss';

.app-switcher {
    all: unset;
    display: inline-flex;
    align-items: center;
    align-self: flex-start;
    gap: 8px;
    padding: 6px 10px 6px 8px;
    border-radius: 10px;
    border: 1.5px solid $border;
    background: $card-background;
    cursor: pointer;
    box-sizing: border-box;
    @include transition-default(background, border-color);

    &:hover {
        background: $card-neutral-background;
    }

    &--open {
        border-color: var(--blue-500);
    }

    &__icon {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 14px;
        height: 14px;
        flex-shrink: 0;

        img,
        svg {
            width: 100%;
            height: 100%;
            display: block;
            object-fit: contain;
        }
    }

    &__name {
        @include fz-14;
        font-weight: 500;
        color: var(--text-high-emphasis);
    }

    &__chevron {
        width: 16px;
        height: 16px;
        color: var(--neutral-400);
        margin-left: auto;
    }
}

.app-switcher-menu {
    display: flex;
    flex-direction: column;
    gap: 4px;
    max-width: calc(100vw - 32px);
}

.app-switcher-menu__item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 12px;
    border-radius: 10px;
    text-decoration: none;
    cursor: pointer;
    @include transition-default(background);

    &:hover {
        background: $card-neutral-background;
    }

    &--active {
        background: $card-neutral-background;

        &:hover {
            background: $card-neutral-background;
        }
    }

    &--disabled {
        pointer-events: none;
        opacity: 0.6;
    }
}

.app-switcher-menu__logo {
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 23px;
    height: 23px;

    img,
    svg {
        width: 100%;
        height: 100%;
        display: block;
        object-fit: contain;
    }
}

.app-switcher-menu__info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.app-switcher-menu__title {
    @include fz-14;
    font-weight: 500;
    color: var(--text-high-emphasis);
}

.app-switcher-menu__desc {
    @include fz-12;
    color: var(--neutral-400);
}

body[theme='dark'] .app-switcher:hover {
    background: $background-secondary;
}

body[theme='dark'] .app-switcher-menu__item:hover,
body[theme='dark'] .app-switcher-menu__item--active {
    background: $background-secondary;
}
</style>
